/**
 * The path a language switch navigates to.
 *
 * Its own module, and not part of `i18n.ts`, so it stays unit-testable: `i18n.ts` reaches the router
 * (and through it the whole route tree, and through that the monolith's SST resources), which no
 * unit test can import.
 */
import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { translateSlug } from './slug-rewrites';

/**
 * Route-id prefix of the redesigned site (`new.worldcoo.com`), which serves each page under ONE
 * slug in every locale — `/es/about-us`, `/ca/about-us`.
 *
 * Slug translation is a www/portal feature BY CONSTRUCTION: the router's `rewrite` picks
 * `wwwSlugs` or `portalSlugs` from the host prefix and never consults the other, so nothing under
 * `/new` is rewritten on the way in or out. `translateSlug`, though, reads a map merged across
 * both contexts, and that is what leaked www's slugs onto the redesign: `about-us` is www's `en`
 * slug, so switching to Catalan on `/es/about-us` pushed `/ca/sobre-nosaltres` — a path only www
 * serves. Solutions never showed the bug for the shallow reason that no www page claims that slug.
 */
const UNLOCALIZED_SLUG_ROUTE_ID = '/new';

/**
 * Whether the matched route's group translates its slugs across locales.
 *
 * Read off the matched route ids rather than the host: the route id is what the rewrite already
 * resolved the host to, so the switcher cannot disagree with it.
 */
function routeLocalizesSlugs(router: { state: { matches: ReadonlyArray<{ routeId: string }> } }): boolean {
	return !router.state.matches.some(
		({ routeId }) => routeId === UNLOCALIZED_SLUG_ROUTE_ID || routeId.startsWith(`${UNLOCALIZED_SLUG_ROUTE_ID}/`),
	);
}

/**
 * Build the path for a given locale, translating slugs as needed.
 * Pure function — no side effects.
 */
export function buildLanguagePath(
	lang: PortalLocale,
	pathname: string,
	search: string,
	currentLocale: string,
	portalId: string,
	router: { options: { context?: unknown }; state: { matches: ReadonlyArray<{ routeId: string }> } },
	currentCampaignId?: string,
): string {
	const segments = pathname.split('/').filter(Boolean);
	const isLocaleFirst = (portalLocales as readonly string[]).includes(segments[0] ?? '');
	const restSegments = isLocaleFirst ? segments.slice(1) : segments;

	let newCampaignSlug: string | undefined;
	if (currentCampaignId) {
		const qc = (router.options.context as { queryClient?: { getQueryData(key: unknown[]): unknown } } | undefined)
			?.queryClient;
		if (qc) {
			const cached = qc.getQueryData([portalId, currentLocale, 'campaigns', currentCampaignId]) as
				| { slugsByLocale?: Record<string, string>; id?: string }
				| undefined;
			newCampaignSlug = cached?.slugsByLocale?.[lang] ?? cached?.id ?? currentCampaignId;
		}
	}

	const newLegalSlug =
		!currentCampaignId && restSegments[0] && routeLocalizesSlugs(router)
			? translateSlug(restSegments[0], lang)
			: undefined;

	const newRestSegments =
		newCampaignSlug !== undefined
			? [newCampaignSlug, ...restSegments.slice(1)]
			: newLegalSlug !== undefined
				? [newLegalSlug, ...restSegments.slice(1)]
				: restSegments;

	return '/' + [lang, ...newRestSegments].join('/') + search;
}
